import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Loader2, AlertCircle, CheckCircle2, Plus, X, Upload, FileCheck, Download } from 'lucide-react';
import appliBg from '../../assets/appliBg.svg';
import { Button } from '../../components/ui/button';
import { useGetInformationRequestByTokenQuery, useSubmitInformationRequestMutation } from '../../store/api/apiSlice';
import type { RequestableField, DocumentFieldValue } from '../../types';
import { validateInvestigationFile, validateDocumentOnlyFile } from '../../utils/fileValidation';

// Column templates for `json` fields, used when there's no prefilled data to
// infer the shape from. Keyed by catalog field key.
const JSON_TEMPLATES: Record<string, string[]> = {
    addressHistory: ['address', 'date'],
    workHistory: ['company', 'role', 'startDate', 'endDate'],
    employmentHistory: ['company', 'role', 'startDate', 'endDate'],
    educationHistory: ['institution', 'qualification', 'year'],
    guarantors: ['name', 'phone', 'relationship', 'address'],
};

// Key templates for `object` fields (native json columns — sent as a real object,
// never stringified). Keyed by catalog field key.
const OBJECT_TEMPLATES: Record<string, string[]> = {
    socialMediaHandles: ['facebook', 'instagram', 'twitter', 'linkedin', 'tiktok', 'other'],
};

function humanize(key: string): string {
    return key
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/^\w/, (c) => c.toUpperCase());
}

// Keys for an `object` field: prefer the shape of any prefilled data, then a
// known template, else fall back to a single free-form entry.
function inferObjectKeys(field: RequestableField, prefill: unknown): string[] {
    const template = OBJECT_TEMPLATES[field.key];
    const keys = new Set<string>(template ?? []);
    if (prefill && typeof prefill === 'object' && !Array.isArray(prefill)) {
        Object.keys(prefill).forEach((k) => keys.add(k));
    }
    return keys.size > 0 ? Array.from(keys) : ['value'];
}

// Rows for a json field are objects of string values keyed by column.
type JsonRows = Record<string, string>[];

function inferColumns(field: RequestableField, prefill: unknown): string[] {
    if (Array.isArray(prefill)) {
        const keys = new Set<string>();
        for (const row of prefill) {
            if (row && typeof row === 'object') Object.keys(row).forEach((k) => keys.add(k));
        }
        if (keys.size > 0) return Array.from(keys);
    }
    return JSON_TEMPLATES[field.key] ?? ['value'];
}

function toRows(prefill: unknown, columns: string[]): JsonRows {
    if (Array.isArray(prefill) && prefill.length > 0) {
        return prefill.map((row) => {
            const out: Record<string, string> = {};
            for (const col of columns) {
                const v = row && typeof row === 'object' ? (row as any)[col] : undefined;
                out[col] = v == null ? '' : String(v);
            }
            return out;
        });
    }
    return [Object.fromEntries(columns.map((c) => [c, ''])) as Record<string, string>];
}

// A single json repeatable-array editor.
function JsonFieldEditor({
    field,
    columns,
    rows,
    onChange,
}: {
    field: RequestableField;
    columns: string[];
    rows: JsonRows;
    onChange: (rows: JsonRows) => void;
}) {
    const addRow = () => onChange([...rows, Object.fromEntries(columns.map((c) => [c, ''])) as Record<string, string>]);
    const removeRow = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
    const updateCell = (i: number, col: string, value: string) =>
        onChange(rows.map((r, idx) => (idx === i ? { ...r, [col]: value } : r)));

    return (
        <div className="flex flex-col gap-3">
            {rows.map((row, i) => (
                <div key={i} className="relative rounded-xl border border-[#e0e0e0] p-3 pr-9">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {columns.map((col) => (
                            <div key={col} className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-[#6b6b6b]">{humanize(col)}</label>
                                <input
                                    type="text"
                                    value={row[col] ?? ''}
                                    onChange={(e) => updateCell(i, col, e.target.value)}
                                    className="rounded-lg border border-[#e0e0e0] px-3 py-2 text-sm focus:border-[#FF6B35] focus:outline-none"
                                />
                            </div>
                        ))}
                    </div>
                    {rows.length > 1 && (
                        <button
                            type="button"
                            onClick={() => removeRow(i)}
                            className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            aria-label="Remove entry"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            ))}
            <button
                type="button"
                onClick={addRow}
                className="flex items-center gap-1.5 self-start text-sm font-medium text-[#FF6B35] hover:text-[#e0581f]"
            >
                <Plus className="h-4 w-4" />
                Add {humanize(field.label).toLowerCase()}
            </button>
        </div>
    );
}

// A signed affidavit must be a real document (PDF/Word), not a photo; everything
// else follows the general investigation-file rules (10 MB, image/pdf/doc/docx).
function validateForField(fieldKey: string, file: File): string | null {
    return fieldKey === 'signedAffidavit' ? validateDocumentOnlyFile(file) : validateInvestigationFile(file);
}

// Upload control for a `document` field. Shows the current file (with a "Replace"
// affordance) when one is already on record, and the newly picked file otherwise.
function DocumentFieldControl({
    field,
    token,
    current,
    selected,
    error,
    onSelect,
}: {
    field: RequestableField;
    token: string;
    current?: DocumentFieldValue | undefined;
    selected?: File | undefined;
    error?: string | undefined;
    onSelect: (file: File | null) => void;
}) {
    const inputRef = useRef<HTMLInputElement>(null);
    const accept =
        field.key === 'signedAffidavit'
            ? 'application/pdf,.pdf,.doc,.docx'
            : field.key === 'passportPhoto'
              ? 'image/jpeg,image/png,image/jpg'
              : 'application/pdf,.pdf,image/jpeg,image/png,image/jpg,.doc,.docx';

    const hasCurrent = current?.uploaded === true;

    // The signed affidavit is a document the employee must download, sign, then
    // re-upload — so surface the blank/personalised affidavit to download first.
    const isAffidavit = field.key === 'signedAffidavit';
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const [templateError, setTemplateError] = useState('');

    const handleDownloadAffidavit = async () => {
        if (!token) return;
        setDownloadingTemplate(true);
        setTemplateError('');
        try {
            const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api/v1';
            const res = await fetch(`${apiBase}/investigations/information-request/${token}/affidavit`);
            if (!res.ok) {
                let message = `Couldn't download the affidavit (HTTP ${res.status}).`;
                try {
                    const json = await res.json();
                    // The API error body may nest `message` as a string, an array of
                    // strings, or an object — never assign a non-string into React state.
                    const raw = json?.message ?? json?.error;
                    if (typeof raw === 'string' && raw.trim()) message = raw;
                    else if (Array.isArray(raw) && raw.length) message = raw.join(', ');
                } catch {
                    /* not JSON */
                }
                setTemplateError(message);
                return;
            }
            const blob = await res.blob();
            const cd = res.headers.get('Content-Disposition') || '';
            const match = cd.match(/filename="?([^";]+)"?/i);
            const fileName = match?.[1] || `affidavit-${token}.pdf`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 1000);
        } catch (err: any) {
            setTemplateError(err?.message || 'Failed to download the affidavit. Please try again.');
        } finally {
            setDownloadingTemplate(false);
        }
    };

    return (
        <div className="flex flex-col gap-1.5">
            {isAffidavit && (
                <div className="mb-1 rounded-lg border border-[#e0e0e0] bg-[#fafafa] px-3 py-2.5">
                    <p className="text-xs text-[#4e4d4d]">
                        Download the affidavit, sign it, then upload the signed copy below. It must be submitted in{' '}
                        <span className="font-semibold text-[#151515]">PDF or Word (.docx)</span> format. A notary
                        public will reach out to you very soon to help complete this.
                    </p>
                    <button
                        type="button"
                        onClick={handleDownloadAffidavit}
                        disabled={downloadingTemplate}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#151515] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#2a2a2a] disabled:opacity-50"
                    >
                        {downloadingTemplate ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Download className="h-3.5 w-3.5" />
                        )}
                        Download affidavit
                    </button>
                    {templateError && <p className="mt-1.5 text-xs text-red-600">{templateError}</p>}
                </div>
            )}

            {selected ? (
                <div className="flex items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5">
                    <FileCheck className="h-4 w-4 shrink-0 text-green-600" />
                    <span className="flex-1 truncate text-sm text-[#1b1b1b]">{selected.name}</span>
                    <button
                        type="button"
                        onClick={() => {
                            if (inputRef.current) inputRef.current.value = '';
                            onSelect(null);
                        }}
                        className="shrink-0 rounded-md p-1 text-red-500 hover:bg-red-50"
                        aria-label="Remove file"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="flex items-center gap-2 rounded-lg border border-dashed border-[#c8c8c8] bg-white px-3 py-2.5 text-left text-sm text-[#4e4d4d] transition-colors hover:border-[#FF6B35] hover:text-[#FF6B35]"
                >
                    <Upload className="h-4 w-4 shrink-0" />
                    <span className="flex-1">
                        {hasCurrent ? 'Replace current file' : `Upload ${field.label.toLowerCase()}`}
                    </span>
                </button>
            )}

            {hasCurrent && !selected && (
                <p className="text-xs text-[#6b6b6b]">
                    Current: <span className="font-medium text-[#1b1b1b]">{current?.fileName || 'uploaded file'}</span>
                </p>
            )}

            {error && <p className="text-xs text-red-600">{error}</p>}

            <input
                ref={inputRef}
                type="file"
                accept={accept}
                onChange={(e) => onSelect(e.target.files?.[0] ?? null)}
                className="hidden"
            />
        </div>
    );
}

export default function InformationRequest() {
    const { token = '' } = useParams();
    const { data: request, isLoading, isError } = useGetInformationRequestByTokenQuery(token, { skip: !token });
    const [submit, { isLoading: isSubmitting }] = useSubmitInformationRequestMutation();

    // One value per requested field. json fields keep JsonRows; others keep primitives.
    const [values, setValues] = useState<Record<string, unknown>>({});
    const [jsonColumns, setJsonColumns] = useState<Record<string, string[]>>({});
    // Newly picked files for `document` fields, keyed by field key.
    const [documentFiles, setDocumentFiles] = useState<Record<string, File>>({});
    const [documentErrors, setDocumentErrors] = useState<Record<string, string>>({});
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    // Seed initial values from the request (prefill from `values`).
    useEffect(() => {
        if (!request) return;
        const seed: Record<string, unknown> = {};
        const cols: Record<string, string[]> = {};
        for (const field of request.fields) {
            const prefill = request.values?.[field.key];
            if (field.type === 'document') {
                // Documents aren't scalar answers — their state comes from request.values
                // ({ uploaded, fileName, ... }) and new files live in `documentFiles`.
                continue;
            }
            if (field.type === 'json') {
                const columns = inferColumns(field, prefill);
                cols[field.key] = columns;
                seed[field.key] = toRows(prefill, columns);
            } else if (field.type === 'object') {
                // Native json column (e.g. socialMediaHandles) — a flat key/value map.
                const keys = inferObjectKeys(field, prefill);
                cols[field.key] = keys;
                const source =
                    prefill && typeof prefill === 'object' && !Array.isArray(prefill) ? (prefill as any) : {};
                seed[field.key] = Object.fromEntries(
                    keys.map((k) => [k, source[k] == null ? '' : String(source[k])]),
                ) as Record<string, string>;
            } else if (field.type === 'boolean') {
                seed[field.key] = prefill === true;
            } else if (field.type === 'yesno') {
                seed[field.key] = prefill === 'yes' || prefill === 'no' ? prefill : '';
            } else {
                seed[field.key] = prefill == null ? '' : String(prefill);
            }
        }
        setValues(seed);
        setJsonColumns(cols);
    }, [request]);

    const grouped = useMemo(() => {
        const map = new Map<string, RequestableField[]>();
        for (const field of request?.fields ?? []) {
            const list = map.get(field.section) ?? [];
            list.push(field);
            map.set(field.section, list);
        }
        return Array.from(map.entries());
    }, [request]);

    const setValue = (key: string, value: unknown) => setValues((prev) => ({ ...prev, [key]: value }));

    const handleSelectDocument = (fieldKey: string, file: File | null) => {
        setDocumentErrors((prev) => {
            const next = { ...prev };
            delete next[fieldKey];
            return next;
        });
        if (!file) {
            setDocumentFiles((prev) => {
                const next = { ...prev };
                delete next[fieldKey];
                return next;
            });
            return;
        }
        const validationError = validateForField(fieldKey, file);
        if (validationError) {
            setDocumentErrors((prev) => ({ ...prev, [fieldKey]: validationError }));
            return;
        }
        setDocumentFiles((prev) => ({ ...prev, [fieldKey]: file }));
    };

    const handleSubmit = async () => {
        if (!request) return;
        setError('');

        // A requested document must be provided — either already on record or newly
        // picked. Block early with a clear per-field message rather than a backend 400.
        const missingDocs = request.fields.filter(
            (f) =>
                f.type === 'document' &&
                !documentFiles[f.key] &&
                (request.values?.[f.key] as DocumentFieldValue | undefined)?.uploaded !== true,
        );
        if (missingDocs.length > 0) {
            setDocumentErrors((prev) => {
                const next = { ...prev };
                for (const f of missingDocs) next[f.key] = 'Please upload this document.';
                return next;
            });
            setError('Please upload the required document(s) before submitting.');
            return;
        }

        // Build the scalar payload with the exact keys that were requested and the
        // correct type-shaped values. Document fields are sent as files, not here.
        const payload: Record<string, unknown> = {};
        for (const field of request.fields) {
            if (field.type === 'document') continue;
            const raw = values[field.key];
            if (field.type === 'json') {
                const rows = Array.isArray(raw) ? (raw as JsonRows) : [];
                payload[field.key] = rows.filter((row) => Object.values(row).some((v) => String(v).trim() !== ''));
            } else if (field.type === 'object') {
                // Send a REAL object (never stringified) — it lands in a native json
                // column. Drop blank keys so we don't persist empty handles.
                const map = raw && typeof raw === 'object' ? (raw as Record<string, string>) : {};
                payload[field.key] = Object.fromEntries(
                    Object.entries(map)
                        .map(([k, v]) => [k, String(v ?? '').trim()])
                        .filter(([, v]) => v !== ''),
                );
            } else if (field.type === 'boolean') {
                payload[field.key] = raw === true;
            } else {
                payload[field.key] = raw ?? '';
            }
        }

        try {
            await submit({
                token,
                values: payload,
                ...(Object.keys(documentFiles).length > 0 ? { documents: documentFiles } : {}),
            }).unwrap();
            setSubmitted(true);
        } catch (err: any) {
            const msg = err?.data?.message ?? err?.message;
            setError(
                Array.isArray(msg)
                    ? msg.join(', ')
                    : typeof msg === 'string'
                      ? msg
                      : 'Could not submit. Please check your answers and try again.',
            );
        }
    };

    // ── States ──────────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#f5f5f5]">
                <Loader2 className="h-10 w-10 animate-spin text-[#FF6B35]" />
            </div>
        );
    }

    if (isError || !request) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#f5f5f5] p-4">
                <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-sm">
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
                        <AlertCircle className="h-7 w-7 text-red-500" />
                    </div>
                    <h1 className="text-xl font-bold text-[#151515]">Link not found</h1>
                    <p className="mt-2 text-sm text-[#4e4d4d]">
                        This information request link is invalid or has expired. Please check the link in your email.
                    </p>
                </div>
            </div>
        );
    }

    const alreadyDone = submitted || request.status === 'completed';
    if (alreadyDone) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#f5f5f5] p-4">
                <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-sm">
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
                        <CheckCircle2 className="h-7 w-7 text-green-600" />
                    </div>
                    <h1 className="text-xl font-bold text-[#151515]">All done</h1>
                    <p className="mt-2 text-sm text-[#4e4d4d]">
                        Thanks{request.employeeName ? `, ${request.employeeName}` : ''}. Your information has been
                        submitted. You can close this page.
                    </p>
                </div>
            </div>
        );
    }

    // ── Form ────────────────────────────────────────────────────────────────
    return (
        <div className="flex min-h-screen items-center justify-center bg-[#f5f5f5] p-4 md:p-10">
            <div className="relative flex w-full max-w-[900px] flex-col overflow-hidden rounded-2xl bg-white md:rounded-3xl">
                <img
                    src={appliBg}
                    alt=""
                    className="absolute right-0 top-0 hidden h-[280px] w-auto rounded-tr-3xl object-contain lg:block"
                />
                <div
                    className="absolute right-0 top-0 hidden h-[280px] w-[500px] rounded-tr-3xl lg:block"
                    style={{
                        backgroundImage:
                            'linear-gradient(92deg, rgb(255,255,255) 14%, rgba(255,255,255,0.886) 48%, rgba(255,255,255,0) 110%)',
                    }}
                />

                {/* Header */}
                <div className="relative z-10 px-6 pt-8 md:px-12 md:pt-12">
                    <p className="text-lg font-bold text-[#acacac]">Hi {request.employeeName || 'there'}</p>
                    <h1 className="mt-2 text-2xl font-bold text-[#151515] md:text-3xl">
                        A little more information is needed
                    </h1>
                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#4e4d4d]">
                        Your background check is almost complete. Please fill in the fields below to help us finish.
                    </p>
                    {request.note && (
                        <div className="mt-4 rounded-xl border border-orange-100 bg-orange-50 px-4 py-3">
                            <p className="text-sm text-[#a85015]">{request.note}</p>
                        </div>
                    )}
                </div>

                {/* Fields */}
                <div className="relative z-10 flex flex-col gap-7 px-6 py-8 md:px-12">
                    {grouped.map(([section, fields]) => (
                        <div key={section} className="flex flex-col gap-4">
                            <h2 className="text-base font-bold text-[#0f0f0f]">{section}</h2>
                            {fields.map((field) => (
                                <div key={field.key} className="flex flex-col gap-1.5 text-left">
                                    <label className="text-sm font-bold text-[#151515]">{field.label}</label>

                                    {field.type === 'text' && (
                                        <input
                                            type="text"
                                            value={(values[field.key] as string) ?? ''}
                                            onChange={(e) => setValue(field.key, e.target.value)}
                                            className="rounded-lg border border-[#e0e0e0] px-3 py-2.5 text-sm focus:border-[#FF6B35] focus:outline-none"
                                        />
                                    )}

                                    {field.type === 'textarea' && (
                                        <textarea
                                            rows={3}
                                            value={(values[field.key] as string) ?? ''}
                                            onChange={(e) => setValue(field.key, e.target.value)}
                                            className="resize-none rounded-lg border border-[#e0e0e0] px-3 py-2.5 text-sm focus:border-[#FF6B35] focus:outline-none"
                                        />
                                    )}

                                    {field.type === 'date' && (
                                        <input
                                            type="date"
                                            value={(values[field.key] as string) ?? ''}
                                            onChange={(e) => setValue(field.key, e.target.value)}
                                            className="rounded-lg border border-[#e0e0e0] px-3 py-2.5 text-sm focus:border-[#FF6B35] focus:outline-none"
                                        />
                                    )}

                                    {field.type === 'yesno' && (
                                        <div className="flex gap-3">
                                            {(['yes', 'no'] as const).map((opt) => {
                                                const active = values[field.key] === opt;
                                                return (
                                                    <button
                                                        key={opt}
                                                        type="button"
                                                        onClick={() => setValue(field.key, opt)}
                                                        className={`rounded-lg border px-5 py-2 text-sm font-medium capitalize transition-colors ${
                                                            active
                                                                ? 'border-[#FF6B35] bg-[#FF6B35]/5 text-[#FF6B35]'
                                                                : 'border-[#e0e0e0] text-[#151515] hover:border-gray-300'
                                                        }`}
                                                    >
                                                        {opt}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {field.type === 'boolean' && (
                                        <button
                                            type="button"
                                            onClick={() => setValue(field.key, !(values[field.key] === true))}
                                            className="flex items-center gap-2.5 self-start"
                                        >
                                            <span
                                                className={`relative h-6 w-11 rounded-full transition-colors ${
                                                    values[field.key] === true ? 'bg-[#FF6B35]' : 'bg-gray-300'
                                                }`}
                                            >
                                                <span
                                                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                                                        values[field.key] === true
                                                            ? 'translate-x-[22px]'
                                                            : 'translate-x-0.5'
                                                    }`}
                                                />
                                            </span>
                                            <span className="text-sm text-[#4e4d4d]">
                                                {values[field.key] === true ? 'Yes' : 'No'}
                                            </span>
                                        </button>
                                    )}

                                    {field.type === 'json' && (
                                        <JsonFieldEditor
                                            field={field}
                                            columns={jsonColumns[field.key] ?? ['value']}
                                            rows={(values[field.key] as JsonRows) ?? []}
                                            onChange={(rows) => setValue(field.key, rows)}
                                        />
                                    )}

                                    {field.type === 'object' && (
                                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                            {(jsonColumns[field.key] ?? ['value']).map((key) => (
                                                <div key={key} className="flex flex-col gap-1">
                                                    <label className="text-xs font-medium text-[#6b6b6b]">
                                                        {humanize(key)}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={
                                                            (values[field.key] as Record<string, string>)?.[key] ?? ''
                                                        }
                                                        onChange={(e) =>
                                                            setValue(field.key, {
                                                                ...((values[field.key] as Record<string, string>) ??
                                                                    {}),
                                                                [key]: e.target.value,
                                                            })
                                                        }
                                                        placeholder="Leave blank if none"
                                                        className="rounded-lg border border-[#e0e0e0] px-3 py-2 text-sm focus:border-[#FF6B35] focus:outline-none"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {field.type === 'document' && (
                                        <DocumentFieldControl
                                            field={field}
                                            token={token}
                                            current={request.values?.[field.key] as DocumentFieldValue | undefined}
                                            selected={documentFiles[field.key]}
                                            error={documentErrors[field.key]}
                                            onSelect={(file) => handleSelectDocument(field.key, file)}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}

                    {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="mt-2 flex items-center justify-center gap-2 self-start rounded-xl bg-[#151515] px-8 py-3 text-sm font-medium text-white hover:bg-[#2a2a2a] disabled:opacity-50"
                    >
                        {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                        Submit
                    </Button>
                </div>
            </div>
        </div>
    );
}
